import axios from "axios";
import { API_BASE_URL } from "../config/apiConfig";
import toast from "react-hot-toast";
import { refreshTokenApi } from "../services/auth/authService";
import { getDecodedToken } from "../utils/authToken";

// ================= AXIOS INSTANCE =================
const apiClient = axios.create({
  baseURL: API_BASE_URL,
});

// ================= TOKEN HELPERS =================
const getToken = () => localStorage.getItem("token");
const getRefreshToken = () => localStorage.getItem("refreshToken");

const saveTokens = (token: string, refreshToken: string) => {
  localStorage.setItem("token", token);
  localStorage.setItem("refreshToken", refreshToken);
};

const clearTokens = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("refreshToken");
};

// ================= REQUEST INTERCEPTOR =================
apiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    const decoded = getDecodedToken();

    //  attach access token
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    //  attach tenant
    const selectedTenant = localStorage.getItem("tenantId");

    if (selectedTenant) {
      config.headers["X-Tenant-Id"] = selectedTenant;
    } else if (decoded?.tenant_id) {
      config.headers["X-Tenant-Id"] = decoded.tenant_id;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ================= RESPONSE INTERCEPTOR =================
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // 🔥 HANDLE 401 → REFRESH TOKEN
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const token = getToken();
        const refreshToken = getRefreshToken();

        if (!token || !refreshToken) {
          throw new Error("Missing tokens");
        }

        const res = await refreshTokenApi(token, refreshToken);

        // ✅ STRICT VALIDATION (fixes your TS error)
        if (!res?.token || !res?.refreshToken) {
          throw new Error("Invalid refresh response");
        }

        // ✅ SAVE NEW TOKENS
        saveTokens(res.token, res.refreshToken);

        // ✅ RETRY ORIGINAL REQUEST
        originalRequest.headers.Authorization = `Bearer ${res.token}`;

        return apiClient(originalRequest);
      } catch (refreshError) {
        // ❌ refresh failed → logout
        clearTokens();
        window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }

    // ================= ERROR MESSAGE =================
    const message =
      error.response?.data?.message ||
      error.response?.data?.title ||
      "Something went wrong";

    toast.error(message);

    return Promise.reject(error);
  }
);

export default apiClient;